import { h, uid } from "../shared/safe";
import { errorBanner } from "../shared/blocks";
import { platformLabel } from "../../platforms";
import { actionButton, noticeBlock, section, statusPill, webLink } from "./bits";
import type { Ctx } from "./context";
import { siteTone, type SatelliteView } from "./model";
import { summariseHosts } from "./derive";

// Per-service satellites. Behaviour kept from the old page: one accordion per satellite, tick the platforms to deploy
// to (already-deployed ones pre-ticked), "Deploy selected", and a "+ New satellite" picker for services that have none.
// What changed: the accordion is a real disclosure (aria-expanded), its open/ticked state survives repaints, results
// are shown per platform as text, and every action is single-flight.

function defaultChecked(sat: SatelliteView): Set<string> {
  return new Set(sat.sites.map((s) => s.platform));
}

function renderSatellite(ctx: Ctx, sat: SatelliteView, hosts: { platform: string }[]): HTMLElement {
  const { state, actions } = ctx;
  const open = state.satelliteOpen.has(sat.id);
  const checked = state.satelliteChecked.get(sat.id) ?? defaultChecked(sat);
  const bodyId = uid("cd-sat-body");
  const deployKey = `satellite:${sat.id}`;
  const live = sat.sites.filter((s) => s.status === "live").length;
  const head = h(
    "button",
    {
      type: "button",
      class: "accordion-head",
      "aria-expanded": open ? "true" : "false",
      "aria-controls": bodyId,
      dataset: { focus: `sat-head:${sat.id}` },
      on: {
        click: () => {
          if (state.satelliteOpen.has(sat.id)) state.satelliteOpen.delete(sat.id);
          else state.satelliteOpen.add(sat.id);
          actions.repaint("satellites");
        },
      },
    },
    h("span", { class: "chevron", "aria-hidden": "true" }, "▸"),
    h("span", { class: "title" }, sat.name),
    h("span", { class: "tag" }, sat.status),
    h("span", { class: "subtitle" }, `${live} platform${live === 1 ? "" : "s"} live · ${sat.slug}`),
  );
  const siteTable = sat.sites.length
    ? h(
        "table",
        { class: "qf-cd-sat-table" },
        h("thead", null, h("tr", null, h("th", null, "Platform"), h("th", null, "URL"), h("th", null, "Status"))),
        h("tbody", null, ...sat.sites.map((s) => h("tr", null, h("td", null, platformLabel(s.platform)), h("td", null, webLink(s.url)), h("td", null, statusPill(s.status, siteTone(s.status)))))),
      )
    : h("p", { class: "muted" }, "Not deployed to any platform yet.");
  const boxes = hosts.map((p) => {
    const id = uid("cd-sat-cb");
    const box = h("input", {
      type: "checkbox",
      id,
      value: p.platform,
      checked: checked.has(p.platform) ? true : false,
      dataset: { focus: `sat-cb:${sat.id}:${p.platform}` },
      on: {
        change: () => {
          const set = new Set(state.satelliteChecked.get(sat.id) ?? defaultChecked(sat));
          if ((box as HTMLInputElement).checked) set.add(p.platform);
          else set.delete(p.platform);
          state.satelliteChecked.set(sat.id, set);
        },
      },
    });
    return h("label", { class: "chip", for: id }, box, platformLabel(p.platform));
  });
  const body = h(
    "div",
    { class: "accordion-body", id: bodyId, hidden: !open },
    siteTable,
    h("fieldset", { class: "qf-cd-fieldset" }, h("legend", null, "Deploy to"), h("div", { class: "chip-list" }, ...boxes)),
    actionButton({
      ctx,
      key: deployKey,
      label: "Deploy selected",
      busyLabel: "Deploying…",
      variant: "secondary",
      onClick: () => actions.deploySatellite(sat.id, Array.from(state.satelliteChecked.get(sat.id) ?? defaultChecked(sat))),
    }),
    h("div", { class: "qf-cd-status", "aria-live": "polite" }, ...(state.satelliteNotices.get(sat.id) ?? []).map((n) => noticeBlock(n))),
  );
  return h("div", { class: `accordion${open ? " open" : ""}`, dataset: { satelliteId: sat.id } }, head, body);
}

export function renderSatellites(ctx: Ctx): HTMLElement {
  const { data, state, actions } = ctx;
  const intro = h(
    "p",
    { class: "muted qf-cd-intro" },
    "A separate, narrowly-scoped mini-site for one service — its content only ever talks about that one service, never the rest of the business. Deploy each one to whichever connected clouds you choose, not automatically all of them.",
  );
  if (data.satellites === null) {
    return section("cd-satellites", "Satellites (per-service mini-sites)", intro, errorBanner(`Satellites could not be loaded${data.failures.satellites ? `: ${data.failures.satellites.message}` : ""}.`, { retry: () => void actions.reloadSection("satellites") }));
  }
  const hosts = summariseHosts(data).usableHosts;
  const sats = data.satellites;
  const taken = new Set(sats.map((s) => s.serviceKey));
  const free = data.client.services.filter((s) => !taken.has(s));
  const create = (() => {
    if (hosts.length === 0) return h("p", { class: "muted qf-cd-gap" }, "Connect a cloud host above before creating a satellite to deploy it to.");
    if (free.length === 0) return h("p", { class: "muted qf-cd-gap" }, data.client.services.length > 0 ? "Every service already has its own satellite." : "Add services to this client's Facts before creating a satellite.");
    const selected = free.includes(state.newSatelliteService) ? state.newSatelliteService : free[0];
    const selectId = "cd-new-satellite-service";
    const select = h(
      "select",
      { id: selectId, dataset: { focus: "new-satellite-service" }, on: { change: (ev) => void (state.newSatelliteService = (ev.target as HTMLSelectElement).value) } },
      ...free.map((s) => h("option", { value: s }, s)),
    );
    select.value = selected;
    return h(
      "div",
      { class: "btn-group qf-cd-gap" },
      h("label", { for: selectId, class: "qf-sr-only" }, "Service for the new satellite"),
      select,
      actionButton({ ctx, key: "satellite:create", label: "+ New satellite", busyLabel: "Creating…", variant: "secondary", onClick: () => actions.createSatellite(select.value) }),
    );
  })();
  return section(
    "cd-satellites",
    "Satellites (per-service mini-sites)",
    intro,
    sats.length === 0 ? h("p", { class: "muted" }, "No per-service satellites yet.") : sats.map((s) => renderSatellite(ctx, s, hosts)),
    create,
    h("div", { id: "cd-satellite-result", "aria-live": "polite" }, noticeBlock(state.satelliteCreateNotice)),
  );
}
